import React, { useEffect, useState, useRef } from 'react'
import imageCompression from 'browser-image-compression'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  Icon,
  VStack,
  Image
} from '@chakra-ui/react'
import { FiUpload } from 'react-icons/fi'
import { FaBus } from 'react-icons/fa'
import { MdClose } from 'react-icons/md'
import { useForm } from 'react-hook-form'
import {
  type FirebaseResponse, getNumberOfSignUpsChristmasDinner2025,
  sendChristmasDinner2025SignUp
} from '../../services/firebaseService'
import Swal from 'sweetalert2'
import type { ChristmasDinner2025FormData } from '../../models/ChristmasDinner2025FormData'
import config from '../../config/config'
import { formatGalicianDate } from '../../lib/formatGalicianDate'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { FirebaseStorage } from '../../config/firebase'

const ChristmasDinner2025Form: React.FC = () => {
  const [isSending, setIsSending] = useState(false)
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const [isFull, setIsFull] = useState(false)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [isCompressingImage, setIsCompressingImage] = useState(false)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [busOption, setBusOption] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<ChristmasDinner2025FormData>()

  const handlePhotoChange = async (file: File | null): Promise<void> => {
    if (file != null) {
      setIsCompressingImage(true)
      try {
        // Opciones de compresión
        const options = {
          maxSizeMB: 1.5, // Tamaño máximo 1.5MB para asegurar que quede bajo 2MB
          maxWidthOrHeight: 1920, // Máximo ancho o alto
          useWebWorker: true,
          fileType: file.type
        }

        // Comprimir la imagen
        const compressedFile = await imageCompression(file, options)

        // Crear un nuevo File con el nombre original pero comprimido
        const finalFile = new File([compressedFile], file.name, {
          type: compressedFile.type,
          lastModified: Date.now()
        })

        setPhotoFile(finalFile)

        // Create preview
        const reader = new FileReader()
        reader.onloadend = () => {
          setPhotoPreview(reader.result as string)
          setIsCompressingImage(false)
        }
        reader.readAsDataURL(finalFile)
      } catch (error) {
        console.error('Error comprimiendo imagen:', error)
        // Si falla la compresión, usar el archivo original
        setPhotoFile(file)
        const reader = new FileReader()
        reader.onloadend = () => {
          setPhotoPreview(reader.result as string)
          setIsCompressingImage(false)
        }
        reader.readAsDataURL(file)
      }
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files?.[0] != null) {
      void handlePhotoChange(e.target.files[0])
    }
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      void handlePhotoChange(files[0])
    }
  }

  const handleBoxClick = (): void => {
    fileInputRef.current?.click()
  }

  const handleRemovePhoto = (): void => {
    setPhotoFile(null)
    setPhotoPreview(null)
    if (fileInputRef.current != null) {
      fileInputRef.current.value = ''
    }
  }

  const uploadPhoto = async (file: File, userId: string): Promise<string> => {
    setIsUploadingPhoto(true)
    try {
      const timestamp = Date.now()
      // Sanitizar el nombre del archivo: eliminar espacios y caracteres especiales
      const sanitizedFileName = file.name
        .replaceAll(/\s+/g, '_')
        .replaceAll(/[^a-zA-Z0-9._-]/g, '')
        .toLowerCase()
      const fileName = `christmas-dinner-2025/${userId}_${timestamp}_${sanitizedFileName}`
      const storageRef = ref(FirebaseStorage, fileName)

      await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(storageRef)

      setIsUploadingPhoto(false)
      return downloadURL
    } catch (error) {
      setIsUploadingPhoto(false)
      throw error
    }
  }

  const submitForm = handleSubmit(async (data: ChristmasDinner2025FormData) => {
    setIsSending(true)
    try {
      // Generar un ID temporal para el usuario
      const tempUserId = `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`

      // Subir la foto si existe
      let photoUrl = ''
      if (photoFile != null) {
        photoUrl = await uploadPhoto(photoFile, tempUserId)
      }

      // Agregar la URL de la foto a los datos
      data.photo = photoUrl

      const r = await sendChristmasDinner2025SignUp(data)
      await showAlert(r)

      if (r.success) {
        reset()
        setPhotoFile(null)
        setPhotoPreview(null)
        setBusOption('')
        if (fileInputRef.current != null) {
          fileInputRef.current.value = ''
        }
      }
    } catch (error: unknown) {
      console.error('Error uploading photo:', error)
      await showAlert({ success: false, error: 'Error ao subir a foto' })
    } finally {
      setIsSending(false)
    }
  })

  const showAlert = async (r: FirebaseResponse<string>): Promise<void> => {
    const title = r.success ? 'Grazas!' : 'Ups!'
    const text = r.success
      ? 'A túa confirmación de asistencia foi enviada correctamente!'
      : 'Algo foi mal, por favor, tenta de novo máis tarde.'
    const icon = r.success ? 'success' : 'error'
    const confirmButtonText = 'Pechar'
    await Swal.fire({
      title,
      text,
      icon,
      confirmButtonText
    })
  }

  useEffect(() => {
    setIsCheckingAvailability(true)
    getNumberOfSignUpsChristmasDinner2025()
      .then(async (r: number) => {
        setIsCheckingAvailability(false)
        setIsFull(r >= config.LIMIT_CHRISTMAS_DINNER_2025)
      })
      .catch((error: unknown) => {
        console.error('Error checking availability:', error)
        setIsCheckingAvailability(false)
      })
  }, [])

  const renderFullMessage = (): JSX.Element => (
        <Text>Sentímolo, pero xa se cubriron todas as prazas para a cea de Nadal 2025.</Text>
  )

  const renderCheckingMessage = (): JSX.Element => (
        <Text><Spinner/> Comprobando dispoñibilidade...</Text>
  )

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    void (async () => {
      await submitForm()
    })()
  }

  const renderBusControl = (): JSX.Element => (
        <FormControl isInvalid={errors.bus != null}>
            <FormLabel>¿Precisas bus gratuíto?</FormLabel>
            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                <Box
                    flex="1"
                    position="relative"
                    cursor="pointer"
                    border="2px solid"
                    borderColor={busOption === 'Sí' ? 'green.400' : 'gray.200'}
                    borderRadius="lg"
                    bg={busOption === 'Sí' ? 'green.50' : 'white'}
                    p={4}
                    transition="all 0.2s"
                    _hover={{
                      borderColor: 'green.400',
                      transform: 'translateY(-2px)',
                      shadow: 'md'
                    }}
                    onClick={() => {
                      setBusOption('Sí')
                      setValue('bus', 'Sí', { shouldValidate: true })
                    }}
                >
                    <Input
                        type="radio"
                        value="Sí"
                        {...register('bus', { required: 'Tes que indicar se precisas bus' })}
                        checked={busOption === 'Sí'}
                        onChange={(e) => {
                          setBusOption(e.target.value)
                          setValue('bus', e.target.value, { shouldValidate: true })
                        }}
                        position="absolute"
                        opacity={0}
                    />
                    <VStack spacing={2}>
                        <Icon
                            as={FaBus}
                            w={8}
                            h={8}
                            color={busOption === 'Sí' ? 'green.500' : 'gray.400'}
                        />
                        <Text
                            fontWeight="bold"
                            fontSize="lg"
                            color={busOption === 'Sí' ? 'green.600' : 'gray.700'}
                        >
                            Sí, preciso bus
                        </Text>
                    </VStack>
                </Box>

                <Box
                    flex="1"
                    position="relative"
                    cursor="pointer"
                    border="2px solid"
                    borderColor={busOption === 'Non' ? 'red.400' : 'gray.200'}
                    borderRadius="lg"
                    bg={busOption === 'Non' ? 'red.50' : 'white'}
                    p={4}
                    transition="all 0.2s"
                    _hover={{
                      borderColor: 'red.400',
                      transform: 'translateY(-2px)',
                      shadow: 'md'
                    }}
                    onClick={() => {
                      setBusOption('Non')
                      setValue('bus', 'Non', { shouldValidate: true })
                    }}
                >
                    <Input
                        type="radio"
                        value="Non"
                        {...register('bus', { required: 'Tes que indicar se precisas bus' })}
                        checked={busOption === 'Non'}
                        onChange={(e) => {
                          setBusOption(e.target.value)
                          setValue('bus', e.target.value, { shouldValidate: true })
                        }}
                        position="absolute"
                        opacity={0}
                    />
                    <VStack spacing={2}>
                        <Icon
                            as={MdClose}
                            w={8}
                            h={8}
                            color={busOption === 'Non' ? 'red.500' : 'gray.400'}
                        />
                        <Text
                            fontWeight="bold"
                            fontSize="lg"
                            color={busOption === 'Non' ? 'red.600' : 'gray.700'}
                        >
                            Non, vou por conta propia
                        </Text>
                    </VStack>
                </Box>
            </Stack>
            <Text fontSize="sm" color="gray.500" mt={3} textAlign="center">
                O bus sairá ás 21:00h dende a cervexería The Movie, en Balaídos.
            </Text>
            <FormErrorMessage>
                {errors.bus?.message}
            </FormErrorMessage>
        </FormControl>
  )

  const renderPhotoUploadContent = (): JSX.Element => {
    if (isCompressingImage) {
      return (
                <VStack spacing={3}>
                    <Spinner size="xl" color="blue.500"/>
                    <Text fontWeight="semibold" color="blue.600">
                        Optimizando imaxe...
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                        Isto pode tardar uns segundos
                    </Text>
                </VStack>
      )
    }

    if (photoPreview === null) {
      return (
                <VStack spacing={3}>
                    <Icon
                        as={FiUpload}
                        w={12}
                        h={12}
                        color={isDragging ? 'blue.400' : 'gray.400'}
                    />
                    <VStack spacing={1}>
                        <Text fontWeight="semibold" color="gray.700">
                            Arrastra unha foto aquí
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            ou fai clic para seleccionar
                        </Text>
                    </VStack>
                    <Text fontSize="xs" color="gray.400">
                        PNG, JPG ou GIF (max. 2MB)
                    </Text>
                    <Text fontSize="xs" color="blue.500" fontWeight="medium">
                        A imaxe optimizarase automaticamente
                    </Text>
                </VStack>
      )
    }

    return (
            <VStack spacing={3}>
                <Image
                    src={photoPreview}
                    alt="Preview"
                    maxH="200px"
                    borderRadius="md"
                    objectFit="cover"
                />
                <VStack spacing={0}>
                    <Text fontSize="sm" color="green.500" fontWeight="medium">
                        {photoFile?.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                        {photoFile === null ? '' : `${(photoFile.size / 1024 / 1024).toFixed(2)} MB`}
                    </Text>
                </VStack>
                <Button
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemovePhoto()
                    }}
                >
                    Eliminar foto
                </Button>
            </VStack>
    )
  }

  const renderForm = (): JSX.Element => (
        <form onSubmit={handleFormSubmit}>
            <Stack spacing={4}>
                <FormControl isInvalid={errors.name != null}>
                    <FormLabel>Nome</FormLabel>
                    <Input
                        type="text"
                        placeholder="Nome"
                        {...register('name', {
                          required: 'Tes que indicar un nome',
                          minLength: {
                            value: 2,
                            message: 'O nome debe ter polo menos 2 caracteres'
                          }
                        })}
                    />
                    <FormErrorMessage>
                        {errors.name?.message}
                    </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={errors.lastName != null}>
                    <FormLabel>Apelidos</FormLabel>
                    <Input
                        type="text"
                        placeholder="Apelidos"
                        {...register('lastName', {
                          required: 'Tes que indicar os apelidos',
                          minLength: {
                            value: 2,
                            message: 'Os apelidos deben ter polo menos 2 caracteres'
                          }
                        })}
                    />
                    <FormErrorMessage>
                        {errors.lastName?.message}
                    </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={errors.email != null}>
                    <FormLabel>E-mail</FormLabel>
                    <Input
                        type="text"
                        placeholder="E-mail"
                        {...register('email', {
                          required: 'Tes que indicar un e-mail',
                          pattern: {
                            value: /\S+@\S+\.\S+/,
                            message: 'Tes que indicar un e-mail válido'
                          }
                        })}
                    />
                    <FormErrorMessage>
                        {errors.email?.message}
                    </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={errors.phone != null}>
                    <FormLabel>Teléfono</FormLabel>
                    <Input
                        type="text"
                        placeholder="Teléfono"
                        {...register('phone', {
                          required: 'Tes que indicar un teléfono',
                          pattern: {
                            value: /^\d{9}$/,
                            message: 'Tes que indicar un teléfono válido'
                          }
                        })}
                    />
                    <FormErrorMessage>
                        {errors.phone?.message}
                    </FormErrorMessage>
                </FormControl>
                {renderBusControl()}

                {/* Mensaje informativo sobre adaptaciones del menú */}
                <Box bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
                    <HStack align="start" spacing={3}>
                        <Icon as={FiUpload} w={5} h={5} color="blue.500" mt={0.5}/>
                        <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="blue.800" fontWeight="semibold">
                                Necesitas adaptar o menú?
                            </Text>
                            <Text fontSize="sm" color="blue.700" lineHeight="1.6">
                                Se precisas menú vexetariano ou calquera outra adaptación, ponte en contacto connosco
                                antes do <Text as="span" fontWeight="bold">{formatGalicianDate(config.FORM_CHRISTMAS_DINNER_2025_DEACTIVATION_DATE)}</Text> a través do correo <Text
                                as="span" fontWeight="bold">lechuzascelestes@gmail.com</Text>
                            </Text>
                        </VStack>
                    </HStack>
                </Box>
                <FormControl isInvalid={photoFile == null && errors.photo != null}>
                    <FormLabel>Foto (opcional)</FormLabel>
                    <Text fontSize="sm" color="gray.600" mb={3} lineHeight="1.6">
                        Súbenos un selfie ou unha foto na que saias ti e só ti, na túa mellor versión (ou na peor, tamén
                        vale), para gardala no noso arquivo. 📸
                    </Text>
                    <Box
                        position="relative"
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={handleBoxClick}
                        cursor="pointer"
                        border="2px dashed"
                        borderColor={isDragging ? 'blue.400' : 'gray.300'}
                        borderRadius="lg"
                        bg={isDragging ? 'blue.50' : 'gray.50'}
                        p={6}
                        transition="all 0.2s"
                        _hover={{
                          borderColor: 'blue.400',
                          bg: 'blue.50'
                        }}
                    >
                        <Input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileInputChange}
                            display="none"
                            disabled={isCompressingImage}
                        />
                        {renderPhotoUploadContent()}
                    </Box>
                    <FormErrorMessage>
                        {errors.photo?.message}
                    </FormErrorMessage>
                </FormControl>
                <hr/>

                {/* Información de pago */}
                <Box bg="green.50" p={4} borderRadius="md" border="2px solid" borderColor="green.200">
                    <VStack align="start" spacing={2}>
                        <Text fontSize="md" fontWeight="bold" color="green.800">
                            💳 Pago por adiantado mediante transferencia
                        </Text>
                        <Text fontSize="sm" color="green.700">
                            <Text as="span" fontWeight="semibold">IBAN:</Text> ES87 0182 5334 9102 0152 1505
                        </Text>
                        <Text fontSize="sm" color="green.700">
                            <Text as="span" fontWeight="semibold">Asunto:</Text> Nome e apelidos dos comensais
                        </Text>
                    </VStack>
                </Box>

                {/* Información de cierre */}
                <Box bg="orange.50" p={4} borderRadius="md" border="2px solid" borderColor="orange.200">
                    <Text fontSize="sm" color="orange.800" fontWeight="medium" textAlign="center">
                        ⏰ Peche ao esgotar aforo ou o día {formatGalicianDate(config.FORM_CHRISTMAS_DINNER_2025_DEACTIVATION_DATE)}
                    </Text>
                </Box>

                <Stack spacing={10}>
                    {(isSending || isUploadingPhoto)
                      ? (
                            <Text><Spinner/> {isUploadingPhoto ? 'Subindo foto...' : 'Enviando datos...'}</Text>
                        )
                      : (
                            <Button
                                type="submit"
                                bg={'blue.400'}
                                color={'white'}
                                _hover={{
                                  bg: 'blue.500'
                                }}>
                                Inscribirse
                            </Button>
                        )}
                </Stack>
            </Stack>
        </form>
  )

  const renderFormContent = (): JSX.Element => {
    if (isFull) {
      return renderFullMessage()
    }

    if (isCheckingAvailability) {
      return renderCheckingMessage()
    }

    return renderForm()
  }

  return (
        <Flex
            align={'center'}
            justify={'center'}
            bg={useColorModeValue('gray.50', 'gray.800')}>
            <Stack spacing={8} mx={'auto'} maxW={'lg'} py={12} px={6} mt={'70px'}>
                <Stack align={'center'}>
                    <Heading
                        fontWeight={600}
                        fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
                        lineHeight={'110%'}>
                        Cea de Nadal 2025&nbsp;<br/>
                        <Text as={'span'} color={'cyan.400'}>
                            Lechuzas Celestes
                        </Text>
                    </Heading>
                    <Heading fontSize={'l'}>
                        Cubre os seguintes datos para inscribirte á cea de Nadal 2025. <br/>
                        13 de decembro ás 22:00h no Pino Manso.<br/>
                        <div style={{ textAlign: 'center', padding: '10px' }}>
                            <iframe
                                title="Mapa de localización do Pino Manso"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1023.4048550130171!2d-8.636015644036156!3d42.22269631381528!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd2587f9453b963f%3A0x765f9dc967e6e1a2!2sO%20Noso%20Rinc%C3%B3n%20Do%20Pino%20Manso!5e1!3m2!1sgl!2ses!4v1730734253437!5m2!1sgl!2ses"
                                loading="lazy"
                                style={{ width: '100%', height: '250px' }}
                                referrerPolicy="no-referrer-when-downgrade"></iframe>
                        </div>
                    </Heading>
                </Stack>
                <Box
                    rounded={'lg'}
                    bg={useColorModeValue('white', 'gray.700')}
                    boxShadow={'lg'}
                    p={8}>
                    {renderFormContent()}
                </Box>
            </Stack>
        </Flex>
  )
}

export default ChristmasDinner2025Form
